import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { account } from "@planzone/dto";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  account: account,
  projectResources: [],
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { account, projectResources }: Boundaries["retrieveProjectsRoles"]["success"],
): State {
  return { account, projectResources, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleEditProjectResource(
  state: State,
  projectResource: Boundaries["editProjectResource"]["success"],
): State {
  const index = state.projectResources.findIndex(
    (req) =>
      req.project.uuid === projectResource.project.uuid &&
      projectResource.uuid === req.resource.uuid,
  );

  return {
    ...state,
    projectResources:
      index !== -1
        ? [
            ...state.projectResources.slice(0, index),
            { ...state.projectResources[index], role: projectResource.role },
            ...state.projectResources.slice(index + 1),
          ]
        : state.projectResources,
  };
}

function handleRetrieveProjectResources(
  state: State,
  { projectResources }: Boundaries["retrieveProjectResources"]["success"],
): State {
  return {
    ...state,
    projectResources: [
      ...state.projectResources,
      ...projectResources
        .filter(({ resource }) => resource.uuid === state.account.resource.uuid)
        .filter(
          ({ uuid }) =>
            !state.projectResources.find(
              (projectResource) => projectResource.uuid === uuid,
            ),
        ),
    ],
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectsRolesRequested]: handleRequest,
    [events.retrieveProjectsRolesSucceeded]: handleSuccess,
    [events.retrieveProjectsRolesFailed]: handleFailure,
    [events.editProjectResourceSucceeded]: handleEditProjectResource,
    [events.retrieveProjectResourcesSucceeded]: handleRetrieveProjectResources,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
